import { useCallback, useEffect, useRef } from 'react'

import type {
  AssignedWorkflow,
  PendingWorkflow,
  WorkflowAction,
} from '../../../shared/hooks/useIncidentWorkflow'
import { useLightMode } from '@/hooks/useLightMode'

import { tokenVar, useEscapeKey } from '@hivewatch/dark'

import { IncidentWorkflowStatus } from '@/graphql/generated/schemas'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeWorkflowDrawer,
  selectWorkflowDrawerIsOpen,
} from '@/redux/ui/uiSlice'

import { INCIDENT_DRAWER_WIDTH } from '../../../shared/constants/drawerDimensions'
import { IncidentDrawerContainer } from '../../components/IncidentDrawerContainer'
import { WorkflowDrawerBody } from './WorkflowDrawerBody'
import { WorkflowDrawerHeader } from './WorkflowDrawerHeader'

export const WORKFLOW_DRAWER_WIDTH = 445
export const WORKFLOW_DRAWER_BREAKPOINT =
  INCIDENT_DRAWER_WIDTH + WORKFLOW_DRAWER_WIDTH

interface WorkflowDrawerIProps {
  assignedWorkflow: AssignedWorkflow | null
  currentStepId: string | null
  hasWorkflows: boolean
  incidentId: string | null
  isActiveOwner: boolean
  isLoadingWorkflow: boolean
  pendingWorkflow: PendingWorkflow | null
  pendingWorkflowFull: AssignedWorkflow | null
  workflowActions: WorkflowAction[]
  workflowStatus: IncidentWorkflowStatus | null
}

export const WorkflowDrawer = ({
  assignedWorkflow,
  currentStepId,
  hasWorkflows,
  incidentId,
  isActiveOwner,
  isLoadingWorkflow,
  pendingWorkflow,
  pendingWorkflowFull,
  workflowActions,
  workflowStatus,
}: WorkflowDrawerIProps) => {
  const isOpen = useAppSelector(selectWorkflowDrawerIsOpen)
  const dispatch = useAppDispatch()
  const mode = useLightMode()
  const isDark = mode === 'dark'
  const drawerBg = isDark ? tokenVar('colors.bg.card') : 'white'

  // Close the drawer if the current incident no longer has workflows
  // (e.g. navigating to a different incident).
  useEffect(() => {
    if (isOpen && !hasWorkflows) {
      dispatch(closeWorkflowDrawer())
    }
  }, [hasWorkflows, isOpen])

  // Escape closes this sub-drawer only — the incident drawer behind it stays
  // open. Joins the dark package's escape stack so nesting resolves. The ref
  // scopes the foreign-dialog guard: a Chakra v1 dialog opened from the action
  // bar (Emergency Call) traps focus without joining the stack, and must keep
  // Escape rather than have it close this drawer behind it. ENG-32100
  const containerRef = useRef<HTMLDivElement>(null)
  const handleEscape = useCallback(
    () => dispatch(closeWorkflowDrawer()),
    [dispatch]
  )
  // Boolean(): `enabled` is a defaulted param, so an `undefined` here would
  // fall back to `true` and arm the listener while the drawer is closed.
  useEscapeKey(handleEscape, Boolean(isOpen && hasWorkflows), containerRef)

  if (!isOpen || !hasWorkflows) return null

  return (
    <IncidentDrawerContainer
      bgColor={drawerBg}
      borderColor={
        isDark
          ? tokenVar('colors.border.subtle')
          : tokenVar('colors.light.border.subtle')
      }
      containerRef={containerRef}
      dataTestid='WorkflowDrawer:wrapper'
      width={WORKFLOW_DRAWER_WIDTH}
    >
      <WorkflowDrawerHeader
        mode={mode}
        onClose={() => dispatch(closeWorkflowDrawer())}
      />
      <WorkflowDrawerBody
        assignedWorkflow={assignedWorkflow}
        currentStepId={currentStepId}
        incidentId={incidentId}
        isActiveOwner={isActiveOwner}
        isLoadingWorkflow={isLoadingWorkflow}
        pendingWorkflow={pendingWorkflow}
        pendingWorkflowFull={pendingWorkflowFull}
        workflowActions={workflowActions}
        workflowStatus={workflowStatus}
      />
    </IncidentDrawerContainer>
  )
}
